import { useEffect, useRef, useState } from 'react'
import Enlace from './Enlace'
import IconoGoogle from './IconoGoogle'
import { useSesion } from '../hooks/useSesion'
import { useEsAdmin } from '../hooks/useEsAdmin'

function AvatarUsuario({ usuario, className = 'size-8' }) {
  if (usuario.photoURL) {
    return (
      <img
        src={usuario.photoURL}
        alt=""
        referrerPolicy="no-referrer"
        className={`${className} rounded-full`}
      />
    )
  }

  const inicial = (usuario.displayName ?? usuario.email ?? '?').trim().charAt(0).toUpperCase()
  return (
    <div
      className={`${className} flex items-center justify-center rounded-full bg-leopardo font-display text-sm text-oro-claro`}
      aria-hidden="true"
    >
      {inicial}
    </div>
  )
}

/** Login con Google en el encabezado: botón cuando no hay sesión, avatar + menú cuando sí. */
function BotonSesion() {
  const { usuario, cargando, iniciarSesion, cerrarSesion } = useSesion()
  const { esAdmin } = useEsAdmin()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const contenedorRef = useRef(null)

  useEffect(() => {
    if (!menuAbierto) return

    function alPulsarFuera(evento) {
      if (!contenedorRef.current?.contains(evento.target)) setMenuAbierto(false)
    }
    function alPulsarTecla(evento) {
      if (evento.key === 'Escape') setMenuAbierto(false)
    }
    document.addEventListener('mousedown', alPulsarFuera)
    document.addEventListener('keydown', alPulsarTecla)
    return () => {
      document.removeEventListener('mousedown', alPulsarFuera)
      document.removeEventListener('keydown', alPulsarTecla)
    }
  }, [menuAbierto])

  if (cargando) {
    return <div className="size-8 animate-pulse rounded-full bg-carbon" aria-hidden="true" />
  }

  if (!usuario) {
    return (
      <button
        type="button"
        onClick={() => iniciarSesion()}
        className="inline-flex min-h-9 items-center gap-2 rounded-sm border border-oro-profundo/40 px-3 text-xs text-marfil-suave transition-colors hover:border-oro-profundo hover:text-oro-claro"
      >
        <IconoGoogle />
        Iniciar sesión
      </button>
    )
  }

  return (
    <div ref={contenedorRef} className="relative">
      <button
        type="button"
        onClick={() => setMenuAbierto((v) => !v)}
        aria-expanded={menuAbierto}
        aria-haspopup="true"
        aria-label="Menú de cuenta"
        className="area-tactil -m-2 block rounded-full p-2"
      >
        <AvatarUsuario usuario={usuario} />
      </button>

      {menuAbierto && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-48 rounded-sm border border-oro-profundo/30 bg-carbon py-2 shadow-lg shadow-noche/50"
        >
          <p className="truncate px-4 pb-2 text-xs text-marfil-suave/60">
            {usuario.displayName ?? usuario.email}
          </p>
          <Enlace
            href="/perfil"
            role="menuitem"
            onClick={() => setMenuAbierto(false)}
            className="block px-4 py-2 text-sm text-marfil-suave transition-colors hover:text-oro-claro"
          >
            Mi perfil
          </Enlace>
          {esAdmin && (
            <Enlace
              href="/admin"
              role="menuitem"
              onClick={() => setMenuAbierto(false)}
              className="block px-4 py-2 text-sm text-marfil-suave transition-colors hover:text-oro-claro"
            >
              Panel admin
            </Enlace>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setMenuAbierto(false)
              cerrarSesion()
            }}
            className="block w-full px-4 py-2 text-left text-sm text-marfil-suave transition-colors hover:text-oro-claro"
          >
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  )
}

export default BotonSesion
